import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { pointsApi } from "@/lib/partnersApi";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Coins, ShoppingCart, Lock } from "@phosphor-icons/react";

// Maps a gate feature to a friendly label + points-config cost field.
const FEATURE_META = {
  jobs:     { label: "Jobs & Careers",       costField: "jobs_access_cost"     },
  partners: { label: "Business Partners",    costField: "partners_access_cost" },
  schools:  { label: "Find School",          costField: "schools_access_cost"  },
};

/**
 * Wraps a premium route. Flow:
 *   1. checkAccess()  — call `GET /points/gate/{feature}/status` to know:
 *        - has_access (already paid this session) → render children immediately
 *        - cost / remaining otherwise → open a confirmation modal
 *   2. On "Continue" the frontend calls `POST /points/gate/{feature}` which
 *      deducts atomically server-side. On 402 → show buy-points screen.
 *   3. If the user Cancels → send them back with `navigate(-1)`.
 */
export default function Gated({ feature, children }) {
  const nav = useNavigate();
  const [state, setState] = useState("checking"); // checking | confirm | granted | denied | charging
  const [info, setInfo] = useState(null);         // { cost, remaining, label }

  const cacheKey = `tain_gate_${feature}`;

  useEffect(() => {
    let cancelled = false;
    if (sessionStorage.getItem(cacheKey) === "1") { setState("granted"); return; }
    (async () => {
      try {
        // Ask backend if we already have access + how much it costs
        const r = await api.get(`/points/gate/${feature}/status`);
        if (cancelled) return;
        if (r.data?.has_access) {
          sessionStorage.setItem(cacheKey, "1");
          setState("granted");
          return;
        }
        setInfo({
          cost: r.data.cost,
          remaining: r.data.remaining,
          label: FEATURE_META[feature]?.label || feature,
        });
        if (r.data.cost <= 0) {
          // Free feature — record access and grant immediately, no prompt.
          sessionStorage.setItem(cacheKey, "1");
          setState("granted");
          return;
        }
        setState("confirm");
      } catch (e) {
        if (e.response?.status === 401) { nav("/", { replace: true }); return; }
        toast.error("Access check failed");
        setState("denied");
      }
    })();
    return () => { cancelled = true; };
  }, [feature, nav, cacheKey]);

  const proceed = async () => {
    setState("charging");
    try {
      const r = await pointsApi.gate(feature);
      sessionStorage.setItem(cacheKey, "1");
      if (r.deducted > 0) toast.success(r.message, { duration: 2500 });
      setState("granted");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 402 && typeof d === "object") {
        setInfo({ ...info, cost: d.cost, remaining: d.remaining });
        setState("denied");
      } else if (e.response?.status === 401) {
        nav("/", { replace: true });
      } else {
        toast.error("Access failed");
        setState("denied");
      }
    }
  };

  const cancel = () => nav(-1);

  if (state === "checking") return <div className="p-8 text-white/50">Verifying access…</div>;

  if (state === "confirm" || state === "charging") return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) cancel(); }}>
      <DialogContent
        data-testid="gate-confirm-modal"
        className="bg-[#121212] border-white/10 text-white max-w-sm"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-yellow-500/15 flex items-center justify-center mx-auto mb-2">
            <Lock size={26} weight="fill" className="text-yellow-300" />
          </div>
          <DialogTitle className="font-heading text-2xl text-center">
            Unlock {info?.label}
          </DialogTitle>
          <DialogDescription className="text-sm text-white/70 text-center mt-1">
            You are about to spend{" "}
            <span className="text-yellow-300 font-black" data-testid="gate-cost">{info?.cost}</span>{" "}
            Credit{info?.cost === 1 ? "" : "s"} to access this section for this session.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm text-center">
          <div className="text-white/50 text-[11px] uppercase tracking-widest font-bold">Your balance</div>
          <div className="font-heading text-2xl font-black text-yellow-300 mt-0.5" data-testid="gate-balance">
            {info?.remaining} cr
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <Button
            data-testid="gate-cancel-btn"
            variant="outline"
            onClick={cancel}
            disabled={state === "charging"}
            className="flex-1 border-white/20 bg-transparent hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            data-testid="gate-continue-btn"
            onClick={proceed}
            disabled={state === "charging"}
            className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-heading font-black gap-2"
          >
            <Coins size={16} weight="fill" />
            {state === "charging" ? "Deducting…" : "Continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (state === "denied") return (
    <div className="max-w-lg mx-auto p-6 md:p-10 mt-8 rounded-2xl border border-yellow-500/30 bg-yellow-500/10 text-center space-y-4" data-testid="gate-denied">
      <Coins size={48} className="mx-auto text-yellow-300" weight="fill" />
      <h1 className="font-heading text-2xl font-black">Not enough Credits</h1>
      <p className="text-white/80">
        {info?.label || "This feature"} costs {info?.cost} Credits. You have {info?.remaining}.
      </p>
      <div className="flex justify-center gap-2">
        <Button data-testid="gate-buy-btn" onClick={() => nav("/buy-points")} className="bg-yellow-500 hover:bg-yellow-600 text-black font-heading font-black gap-2">
          <ShoppingCart size={16} weight="bold" /> Buy Credits
        </Button>
        <Button variant="ghost" onClick={() => nav(-1)}>Back</Button>
      </div>
    </div>
  );

  return children;
}
